//@flow
import { DragSourceConnector, DragSourceMonitor, DropTargetConnector, DropTargetMonitor } from 'react-dnd';
import _ from 'lodash';
import FileInfoUtil from '../util/FileInfoUtil';
import Store from '../store/Store';
import { DRAGGING_ASSET } from '../store/Constants';
import { RepoItem } from '../autogenerated/ZoomBridge';
import ThumbnailUtil from '../util/ThumbnailUtil';
import { EventEmitter } from 'events';

const debug = require('../debug/debug')('wc:dnd:reactDndConfig');

export const dndEmitter = new EventEmitter();

export function createDndConfig(getAsset: Function) {
  return {
    source: {
      spec: {
        beginDrag: (props) => {
          const asset: RepoItem = getAsset(props);
          debug('[beginDrag()] dragging asset:', asset);
          Store.put(DRAGGING_ASSET, asset);
          return { id: asset.getFuid(), name: asset.getName() };
        },
        endDrag: (props, monitor: DragSourceMonitor, component) => {
          const asset = getAsset(props);
          Store.put(DRAGGING_ASSET, null);
          if (!monitor.didDrop()) {
            debug('[endDrag()] !didDrop. return. monitor: ', monitor);
            return;
          }

          const sources = window.gs.selectedAssets.length ? window.gs.selectedAssets : [asset];
          const target = new RepoItem();
          const dropResult = monitor.getDropResult();
          target.deserializeJson(JSON.parse(dropResult.json));
          debug('[endDrag()] sources:', sources);
          debug('[endDrag()] target:', target);

          if (_.includes(sources.map((s) => s.getFuid()), target.getFuid())) {
            debug('[endDrag()] invalid move: cannot drop onto itself');
            return;
          }

          dndEmitter.emit('drop', sources, target);
        }
      },
      collect: (connect: DragSourceConnector, monitor: DragSourceMonitor) => ({
        connectDragSource: connect.dragSource(),
        connectDragPreview: connect.dragPreview(),
        isDragging: monitor.isDragging()
      })
    },
    target: {
      spec: {
        canDrop: (props) => {
          const asset = getAsset(props);
          return FileInfoUtil.isFolder(asset);
        },
        drop: (props, monitor: DropTargetMonitor) => {
          if (monitor.didDrop()) {
            debug('[drop()] drop handled by child. return. monitor: ', monitor);
            return;
          }
          const asset: RepoItem = getAsset(props);
          debug('[drop()] dropped onto asset:', asset);
          return { json: JSON.stringify(asset.serializeJson()) };
        }
      },
      collect: (connect: DropTargetConnector, monitor: DropTargetMonitor) => ({
        connectDropTarget: connect.dropTarget(),
        isOver: !!monitor.isOver({ shallow: true }),
        canDrop: !!monitor.canDrop()
      })
    }
  };
}

export const defaultGetAsset = (props: any) => props.asset;

export const defaultDndConfig = createDndConfig(defaultGetAsset);

export function computeDragPreview(assets: RepoItem[]) {
  return new Promise(async (resolve) => {
    debug('[computeDragPreview()] begin');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const MAX = 11;

    let badge;
    if (assets.length > MAX) {
      try {

      } catch (e) {
        await new Promise((resolve) => {
          const label = `+${assets.length - MAX}`;
          debug('[computeDragPreview()] computing badge. label: ', label);
          badge = document.createElement('img');
          badge.width = 40;
          badge.height = 40;
          badge.onload = () => {
            debug('[computeDragPreview()] badge ready');
            resolve();
          };
          badge.onerror = (e) => {
            debug('[computeDragPreview()] badge error: ', e);
            resolve();
          };

          badge.src =
            'data:image/svg+xml,' +
            encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
            <foreignObject>
              <link xmlns="http://www.w3.org/1999/xhtml" href="//fonts.googleapis.com/css?family=Montserrat:400,600" rel="stylesheet" />
            </foreignObject>
            <rect x="0" y="0" width="40" height="40" fill="#eb6a5c" />
            <text x="45%" y="25" text-anchor="middle" style="fill:#fff; font-size:14px; font-family:Montserrat, sans-serif;">${label}</text>
          </svg>`);
        });
      }

    }

    assets = assets.slice(0, MAX);

    const x1 = Math.sqrt(assets.length);
    const x2 = Math.round(x1);
    const rows = x2;
    const cols = x1 === x2 ? x2 : x2 + 1;
    canvas.width = 45 * cols - 5;
    canvas.height = 45 * rows - 5;
    const urls = assets.map((asset) => ThumbnailUtil.toThumbUrl(asset));
    const promises = urls.map((url, i) => {
      return new Promise((resolve, reject) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const img = document.createElement('img');
        img.width = 40;
        img.height = 40;
        img.onload = () => {
          debug('[computeDragPreview()] img loaded', i);
          ctx.drawImage(img, col * 45, row * 45, 40, 40);
          resolve();
        };
        img.onerror = () => {
          debug('[computeDragPreview()] img error: ', img, i);
          reject();
        };
        img.src = url;
      });
    });

    await Promise.all(promises);
    debug('[computeDragPreview()] thumbnails ready');

    if (badge) {
      ctx.drawImage(badge, 3 * 45, 2 * 45, 40, 40);
    }

    const dragPreview = document.createElement('img');
    dragPreview.width = canvas.width;
    dragPreview.height = canvas.height;
    dragPreview.src = canvas.toDataURL();
    debug('[computeDragPreview()] end');
    return resolve(dragPreview);
  });
}
